// src/components/crm/PayrollSettings.js
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const toDateInput = (date) => {
    if (!date || !(date instanceof Date)) return '';
    return date.toISOString().split('T')[0];
};

const SCHEDULE_PRESETS = {
    '5x8':  { workDaysPerWeek: 5, workHoursPerDay: 8  },
    '4x10': { workDaysPerWeek: 4, workHoursPerDay: 10 },
    '3x12': { workDaysPerWeek: 3, workHoursPerDay: 12 },
};

// Default days pre-selected when a preset is chosen (user can override)
const PRESET_WORK_DAYS = {
    '5x8':  ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    '4x10': ['monday', 'tuesday', 'wednesday', 'thursday'],
    '3x12': ['monday', 'wednesday', 'friday'],
};

const ALL_DAYS = [
    { key: 'sunday',    label: 'Sun' },
    { key: 'monday',    label: 'Mon' },
    { key: 'tuesday',   label: 'Tue' },
    { key: 'wednesday', label: 'Wed' },
    { key: 'thursday',  label: 'Thu' },
    { key: 'friday',    label: 'Fri' },
    { key: 'saturday',  label: 'Sat' },
];

// paidact-const — Phase 3c Seam 2. The four activity types whose clocked time
// can be paid or unpaid. Keys MUST stay { work, travel, break, shop } — they are
// the paidMap keys read by isActivityPaid() in shiftTime.js and must match the
// shift.activityType values. `work` is fixed as always-paid (locked in the UI,
// forced true on save); only travel/break/shop are togglable.
const PAID_ACTIVITIES = [
    { key: 'work',   label: 'Work',   description: 'Regular on-the-job time',        locked: true  },
    { key: 'travel', label: 'Travel', description: 'Driving to a job or between jobs', locked: false },
    { key: 'break',  label: 'Break',  description: 'Lunch and rest breaks',           locked: false },
    { key: 'shop',   label: 'Shop',   description: 'Time at the shop or yard',         locked: false },
];

// abstype-6e2b9d14-a — Absence Seam 1. Built-in "time away" types. A SEPARATE list
// from PAID_ACTIVITIES on purpose: these are never clocked, so they must never reach
// payrollDefaults.customActivities (the mobile clock-in picker reads that map live).
// Labels are FIXED — legacy timeOffHistory entries carry type 'Vacation' | 'Sick' |
// 'Personal' as free text, and these labels are what map them onto these keys.
// Statutory holidays are deliberately NOT here — they come from the province calendar.
const PAID_ABSENCES = [
    { key: 'vacation',    label: 'Vacation',    description: 'Planned time off' },
    { key: 'sick',        label: 'Sick',        description: 'Illness or medical leave' },
    { key: 'personal',    label: 'Personal',    description: 'Personal or family matters' },
    { key: 'training',    label: 'Training',    description: 'Courses or certification away from work' },
    { key: 'bereavement', label: 'Bereavement', description: 'Leave after a death in the family' },
];

// abstype-6e2b9d14-k — Seam 1 ships DARK. While false, the whole "Time away" section is
// not rendered, so no control can change absenceTypes or paidAbsences: absenceTypes is
// never written, and every save writes the five paidAbsences keys as true, which is
// what an absent key already means. Absence Seam 3 (the HR modals read this list) flips
// this to true. Do not flip it before Seam 3 — the list would be offered with nothing
// that uses it.
const SHOW_TIME_AWAY = false;

const DEFAULT_PAYROLL_DEFAULTS = {
    schedulePreset: '5x8',
    workDaysPerWeek: 5,
    workHoursPerDay: 8,
    workDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    travelBufferMinutes: 30,
    shiftTrackingMode: 'estimate',
    // paidact-const — all four default to paid; absent map/key resolves to paid too.
    paidActivities: { work: true, travel: true, break: true, shop: true },
    // custact-const — a business's own activities, keyed by generated id so a
    // rename never breaks a reference. Empty for every existing org until an
    // owner adds one. Value shape: { name, paid, archived, createdAt }.
    customActivities: {},
    // abstype-6e2b9d14-b — time away. Absent key resolves to paid, like activities.
    paidAbsences: { vacation: true, sick: true, personal: true, training: true, bereavement: true },
    // Value shape mirrors customActivities: { name, paid, archived, createdAt }.
    absenceTypes: {},
};

// custact-const — the four reserved activityType literals a custom id must never
// collide with; a Set for the id-generator guard below.
const RESERVED_ACTIVITY_KEYS = new Set(['work', 'travel', 'break', 'shop']);

// custact-const — cap on ACTIVE (non-archived) custom activities. The mobile
// clock-in sheet renders all active ones in one list, so this keeps the picker
// usable. Archived entries don't count. One constant — change freely.
const MAX_CUSTOM_ACTIVITIES = 12;

// custact-const — client-side id for a new custom activity (no addDoc for a map
// key). `act_` + 6 base36 chars: can't equal a reserved literal, and the loop
// guarantees no collision with an id already in this org's map. The id lands on
// payroll records permanently as their activityType, so it's prefixed to be
// recognisable in Firestore; the human label always sits beside it in `name`.
const genActivityId = (existing = {}) => {
    let id;
    do {
        id = 'act_' + Math.random().toString(36).slice(2, 8);
    } while (RESERVED_ACTIVITY_KEYS.has(id) || existing[id]);
    return id;
};

// abstype-6e2b9d14-c — custom time-away ids are `abs_` + 6 base36 chars, a different
// prefix from `act_` so the two id spaces can never collide and a misfiled id is
// visibly wrong. Cap kept equal to activities so the HR dropdown stays short.
const RESERVED_ABSENCE_KEYS = new Set(PAID_ABSENCES.map(a => a.key));
const MAX_ABSENCE_TYPES = 12;
const genAbsenceId = (existing = {}) => {
    let id;
    do {
        id = 'abs_' + Math.random().toString(36).slice(2, 8);
    } while (RESERVED_ABSENCE_KEYS.has(id) || existing[id]);
    return id;
};

const PayrollSettings = ({ currentUserProfile }) => {
    // ── Existing payroll period fields ──
    const [frequency, setFrequency] = useState('weekly');
    const [startDate, setStartDate] = useState('');

    // ── New payroll defaults ──
    const [payrollDefaults, setPayrollDefaults] = useState(DEFAULT_PAYROLL_DEFAULTS);

    const [isLoading, setIsLoading] = useState(true);
    // p1-loadfail-7b1e3d52 — set when the org read fails, so the form is never
    // shown (or saved) over settings that were never loaded.
    const [loadError, setLoadError] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [feedback, setFeedback] = useState({ type: '', message: '' });
    // custact-state — UI-only, never persisted; whether the archived tail is open.
    const [showArchivedActivities, setShowArchivedActivities] = useState(false);
    // abstype-6e2b9d14-d — UI-only; whether the archived time-away tail is open.
    const [showArchivedAbsences, setShowArchivedAbsences] = useState(false);

    const orgId = currentUserProfile?.organizationId;

    useEffect(() => {
        if (!orgId) return;
        const fetchSettings = async () => {
            setIsLoading(true);
            // p1-dotsave-2d9f6a48 — reset to defaults on an org switch (God mode swaps
            // org in place without remounting), so nothing from a previously viewed
            // org survives in state and gets saved into this one.
            setFrequency('weekly');
            setStartDate('');
            setPayrollDefaults(DEFAULT_PAYROLL_DEFAULTS);
            setLoadError(false);
            try {
                const orgDocRef = doc(db, 'organizations', orgId);
                const docSnap = await getDoc(orgDocRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    if (data.payrollSettings) {
                        setFrequency(data.payrollSettings.frequency || 'weekly');
                        if (data.payrollSettings.startDate?.toDate) {
                            setStartDate(toDateInput(data.payrollSettings.startDate.toDate()));
                        }
                    }
                    if (data.payrollDefaults) {
                        setPayrollDefaults(prev => ({ ...prev, ...data.payrollDefaults }));
                    }
                }
            } catch (error) {
                // p1-loadfail-7b1e3d52 — there was no catch here at all, so a failed
                // read left the screen stuck on "Loading settings..." forever with no
                // explanation. Surface it and block saving.
                console.error('Error loading payroll settings:', error);
                setLoadError(true);
            }
            setIsLoading(false);
        };
        fetchSettings();
    }, [orgId]);

    const handleDefaultChange = (field, value) => {
        setPayrollDefaults(prev => {
            const updated = { ...prev, [field]: value };
            if (field === 'schedulePreset' && SCHEDULE_PRESETS[value]) {
                updated.workDaysPerWeek = SCHEDULE_PRESETS[value].workDaysPerWeek;
                updated.workHoursPerDay = SCHEDULE_PRESETS[value].workHoursPerDay;
                // Pre-populate work days from preset — user can then adjust
                if (PRESET_WORK_DAYS[value]) updated.workDays = [...PRESET_WORK_DAYS[value]];
            }
            return updated;
        });
    };

    const handleDayToggle = (dayKey) => {
        setPayrollDefaults(prev => {
            const current = prev.workDays || [];
            const isSelected = current.includes(dayKey);
            // Cap: for named presets use preset max; for custom use the number input value
            const cap = prev.schedulePreset === 'custom'
                ? Number(prev.workDaysPerWeek) || 7
                : SCHEDULE_PRESETS[prev.schedulePreset]?.workDaysPerWeek ?? 5;
            // Block adding if already at cap and this day isn't already selected
            if (!isSelected && current.length >= cap) return prev;
            const next = isSelected
                ? current.filter(d => d !== dayKey)
                : [...current, dayKey];
            return { ...prev, workDays: next, workDaysPerWeek: next.length };
        });
    };

    // paidact-handler — flip one activity's paid flag. Never called for `work`
    // (its UI control is disabled), and save() force-writes work:true regardless.
    // Reads current state as `!== false` so an absent key counts as paid.
    const handlePaidActivityToggle = (key) => {
        setPayrollDefaults(prev => {
            const current = prev.paidActivities?.[key] !== false; // absent = paid
            return {
                ...prev,
                paidActivities: { ...prev.paidActivities, [key]: !current },
            };
        });
    };

    // custact-handler — all edits are local state; nothing writes until Save,
    // exactly like every other field here. An unsaved add is discarded on
    // navigate-away or refresh.
    const patchCustomActivity = (id, patch) => {
        setPayrollDefaults(prev => ({
            ...prev,
            customActivities: {
                ...prev.customActivities,
                [id]: { ...prev.customActivities?.[id], ...patch },
            },
        }));
    };

    const handleAddCustomActivity = () => {
        setPayrollDefaults(prev => {
            const map = prev.customActivities || {};
            const activeCount = Object.values(map).filter(a => a && !a.archived).length;
            if (activeCount >= MAX_CUSTOM_ACTIVITIES) return prev; // cap guard
            const id = genActivityId(map);
            return {
                ...prev,
                customActivities: {
                    ...map,
                    [id]: { name: '', paid: true, archived: false, createdAt: Date.now() },
                },
            };
        });
    };

    const handleRenameCustomActivity = (id, name) => patchCustomActivity(id, { name });
    const handleToggleCustomPaid = (id) =>
        setPayrollDefaults(prev => {
            const current = prev.customActivities?.[id]?.paid !== false; // absent = paid
            return {
                ...prev,
                customActivities: {
                    ...prev.customActivities,
                    [id]: { ...prev.customActivities?.[id], paid: !current },
                },
            };
        });
    const handleArchiveCustomActivity = (id) => patchCustomActivity(id, { archived: true });
    const handleRestoreCustomActivity = (id) => patchCustomActivity(id, { archived: false });

    // abstype-6e2b9d14-e — time away. Local state only until Save, like everything here.
    const handlePaidAbsenceToggle = (key) => {
        setPayrollDefaults(prev => {
            const current = prev.paidAbsences?.[key] !== false; // absent = paid
            return { ...prev, paidAbsences: { ...prev.paidAbsences, [key]: !current } };
        });
    };
    const patchAbsenceType = (id, patch) => {
        setPayrollDefaults(prev => ({
            ...prev,
            absenceTypes: {
                ...prev.absenceTypes,
                [id]: { ...prev.absenceTypes?.[id], ...patch },
            },
        }));
    };
    const handleAddAbsenceType = () => {
        setPayrollDefaults(prev => {
            const map = prev.absenceTypes || {};
            const activeCount = Object.values(map).filter(a => a && !a.archived).length;
            if (activeCount >= MAX_ABSENCE_TYPES) return prev; // cap guard
            const id = genAbsenceId(map);
            return {
                ...prev,
                absenceTypes: {
                    ...map,
                    [id]: { name: '', paid: true, archived: false, createdAt: Date.now() },
                },
            };
        });
    };
    const handleRenameAbsenceType = (id, name) => patchAbsenceType(id, { name });
    const handleToggleAbsencePaid = (id) =>
        setPayrollDefaults(prev => {
            const current = prev.absenceTypes?.[id]?.paid !== false; // absent = paid
            return {
                ...prev,
                absenceTypes: {
                    ...prev.absenceTypes,
                    [id]: { ...prev.absenceTypes?.[id], paid: !current },
                },
            };
        });
    const handleArchiveAbsenceType = (id) => patchAbsenceType(id, { archived: true });
    const handleRestoreAbsenceType = (id) => patchAbsenceType(id, { archived: false });

    const handleSave = async (e) => {
        e.preventDefault();
        // p1-loadfail-7b1e3d52 — never write settings that were never loaded.
        if (loadError) {
            setFeedback({ type: 'error', message: 'Settings could not be loaded, so they cannot be saved. Please reload the page.' });
            return;
        }
        if (!orgId || !startDate) {
            setFeedback({ type: 'error', message: 'Please select a start date.' });
            return;
        }
        // custact-save — an active custom activity with a blank name would render a
        // nameless picker button on mobile, and merge:true can't blank an
        // already-saved name anyway. Block the save and point at the offending row
        // (each blank active row also shows its own red inline hint in the list).
        const blankActive = Object.values(payrollDefaults.customActivities || {})
            .some(a => a && !a.archived && !String(a.name || '').trim());
        if (blankActive) {
            setFeedback({ type: 'error', message: 'One of your activities still needs a name — the empty row is marked in red. Name it, or archive it.' });
            return;
        }
        // abstype-6e2b9d14-f — time-away types: a blank name blocks the save, and so does a
        // name equal to a built-in label (case-insensitive, trimmed). Legacy time-off records
        // map onto the built-ins BY LABEL, so a custom "vacation" would make that mapping
        // ambiguous. Two custom types sharing a name stay a warning only, as for activities.
        const activeAbsenceTypes = Object.values(payrollDefaults.absenceTypes || {})
            .filter(a => a && !a.archived);
        if (activeAbsenceTypes.some(a => !String(a.name || '').trim())) {
            setFeedback({ type: 'error', message: 'One of your time-away types still needs a name — the empty row is marked in red. Name it, or archive it.' });
            return;
        }
        const builtInAbsenceLabels = new Set(PAID_ABSENCES.map(t => t.label.toLowerCase()));
        if (activeAbsenceTypes.some(a => builtInAbsenceLabels.has(String(a.name || '').trim().toLowerCase()))) {
            setFeedback({ type: 'error', message: 'One of your time-away types has the same name as a built-in type — the row is marked in red. Rename it, or archive it.' });
            return;
        }
        setIsSaving(true);
        setFeedback({ type: '', message: '' });
        try {
            const orgDocRef = doc(db, 'organizations', orgId);
            // p1-dotsave-2d9f6a48 — dotted field paths, one per owned key, instead of
            // handing setDoc whole `payrollSettings` / `payrollDefaults` maps rebuilt
            // from a snapshot taken when the screen opened. That older shape reverted
            // any key another admin had changed since. Dotted paths REQUIRE updateDoc
            // (setDoc would create a field literally named "payrollDefaults.workDays");
            // the organization document always exists by the time this screen renders.
            const payload = {
                'payrollSettings.frequency': frequency,
                'payrollSettings.startDate': new Date(startDate),
                'payrollDefaults.schedulePreset': payrollDefaults.schedulePreset,
                'payrollDefaults.shiftTrackingMode': payrollDefaults.shiftTrackingMode,
                'payrollDefaults.workDaysPerWeek': Number(payrollDefaults.workDaysPerWeek) || 5,
                'payrollDefaults.workHoursPerDay': Number(payrollDefaults.workHoursPerDay) || 8,
                'payrollDefaults.workDays': payrollDefaults.workDays || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
                'payrollDefaults.travelBufferMinutes': Number(payrollDefaults.travelBufferMinutes) ?? 30,
                // paidact-save — MOBILE CONTRACT. This exact path,
                // organizations/{orgId}.payrollDefaults.paidActivities, is what
                // Seam 3 in the spatchy-mobile repo reads to build the paidMap for
                // isActivityPaid(). If this path and that reader ever diverge, every
                // activity silently resolves to paid and the setting stops working
                // with no error. Keys must stay { work, travel, break, shop }.
                // work is forced true here so regular work can never persist unpaid,
                // even if the UI control were bypassed.
                // Written as a whole map deliberately: all four keys are always
                // present together and mobile reads them as a unit.
                'payrollDefaults.paidActivities': { ...payrollDefaults.paidActivities, work: true },
            };
            // custact-save — write the map trimmed. Archive-not-delete means we never
            // remove a key, so a reference is never orphaned; the generated id stays
            // stable across a rename.
            // p1-dotsave-2d9f6a48 — ONE dotted path PER ACTIVITY, not the whole map.
            // Writing the map wholesale would delete an activity added on another
            // device since this screen loaded, which is exactly what archive-never-
            // delete exists to prevent. Per-id paths can only add or update.
            Object.entries(payrollDefaults.customActivities || {}).forEach(([id, a]) => {
                payload[`payrollDefaults.customActivities.${id}`] = {
                    ...a,
                    name: String(a?.name || '').trim(),
                };
            });
            // abstype-6e2b9d14-g — time away. One dotted path per built-in key and per custom
            // id. The ONLY guarantee this gives: a save never deletes a type added from
            // another device, because a per-id path can only add or update. It does NOT
            // protect concurrent edits — every save writes all five paidAbsences keys and
            // every absenceTypes id from this screen's local state, so a stale tab overwrites
            // a toggle, rename or archive saved from another tab since it loaded. Never
            // written under customActivities, which is the mobile clock-in list.
            PAID_ABSENCES.forEach(({ key }) => {
                payload[`payrollDefaults.paidAbsences.${key}`] = payrollDefaults.paidAbsences?.[key] !== false;
            });
            Object.entries(payrollDefaults.absenceTypes || {}).forEach(([id, a]) => {
                payload[`payrollDefaults.absenceTypes.${id}`] = {
                    ...a,
                    name: String(a?.name || '').trim(),
                };
            });
            await updateDoc(orgDocRef, payload);
            setFeedback({ type: 'success', message: 'Settings saved successfully!' });
        } catch (error) {
            console.error('Error saving payroll settings:', error);
            setFeedback({ type: 'error', message: 'Failed to save settings. Please try again.' });
        } finally {
            setIsSaving(false);
            setTimeout(() => setFeedback({ type: '', message: '' }), 4000);
        }
    };

    if (isLoading) return <div className="p-6 text-gray-500">Loading settings...</div>;

    // p1-loadfail-7b1e3d52 — show an error instead of a form full of defaults.
    if (loadError) {
        return (
            <div className="p-6">
                <h2 className="text-xl font-bold text-red-700">Couldn't load payroll settings</h2>
                <p className="text-gray-600 mt-2">
                    Your current payroll settings could not be read, so they can't be edited right
                    now — saving would overwrite them with defaults. Please reload the page, and if
                    this keeps happening, contact support.
                </p>
            </div>
        );
    }

    const pd = payrollDefaults;
    const weeklyHours = (Number(pd.workDaysPerWeek) || 5) * (Number(pd.workHoursPerDay) || 8);

    return (
        <div className="bg-white p-6 rounded-lg shadow-md space-y-8">

            {/* ── PAY PERIOD ── */}
            <div>
                <h2 className="text-xl font-bold mb-1">Payroll Settings</h2>
                <p className="text-gray-500 text-sm mb-5">
                    Define the start date and frequency for your company's payroll periods.
                    This is used throughout the Timesheets view.
                </p>
                <div className="space-y-4">
                    <div>
                        <label htmlFor="payrollFrequency" className="block text-sm font-medium text-gray-700">
                            Payroll Frequency
                        </label>
                        <select
                            id="payrollFrequency"
                            value={frequency}
                            onChange={e => setFrequency(e.target.value)}
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                        >
                            <option value="weekly">Weekly</option>
                            <option value="bi-weekly">Bi-weekly (Every 2 weeks)</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="payrollStartDate" className="block text-sm font-medium text-gray-700">
                            Payroll Start Date
                        </label>
                        <p className="text-xs text-gray-500 mb-1">Select the start date of any of your pay periods (e.g., a Monday).</p>
                        <input
                            type="date"
                            id="payrollStartDate"
                            value={startDate}
                            onChange={e => setStartDate(e.target.value)}
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                            required
                        />
                    </div>
                </div>
            </div>

            {/* ── PAYROLL DEFAULTS ── */}
            <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-1">Payroll Defaults</h3>
                <p className="text-sm text-gray-500 mb-5">
                    These defaults apply to employees who don't have a personal schedule configured.
                    They control how missing shifts are estimated and how payroll hours are calculated.
                </p>

                {/* Default Work Schedule */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Default Work Schedule</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {[
                            { value: '5x8',    label: '5 × 8h',  sub: '40h week'  },
                            { value: '4x10',   label: '4 × 10h', sub: '40h week'  },
                            { value: '3x12',   label: '3 × 12h', sub: '36h week'  },
                            { value: 'custom', label: 'Custom',  sub: 'set below' },
                        ].map(opt => {
                            const active = (pd.schedulePreset || '5x8') === opt.value;
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => handleDefaultChange('schedulePreset', opt.value)}
                                    className={`flex flex-col items-center justify-center rounded-lg border-2 py-3 px-2 text-sm font-semibold transition-colors
                                        ${active
                                            ? 'border-blue-600 bg-blue-50 text-blue-700'
                                            : 'border-gray-200 bg-white text-gray-600 hover:border-blue-300'
                                        }`}
                                >
                                    {opt.label}
                                    <span className={`text-xs font-normal mt-0.5 ${active ? 'text-blue-500' : 'text-gray-400'}`}>
                                        {opt.sub}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {pd.schedulePreset === 'custom' && (
                        <div className="grid grid-cols-2 gap-3 mt-3">
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Days per week</label>
                                <input
                                    type="number" min="1" max="7"
                                    value={pd.workDaysPerWeek ?? 5}
                                    onChange={e => handleDefaultChange('workDaysPerWeek', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Hours per day</label>
                                <input
                                    type="number" min="1" max="24" step="0.5"
                                    value={pd.workHoursPerDay ?? 8}
                                    onChange={e => handleDefaultChange('workHoursPerDay', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded text-sm"
                                />
                            </div>
                        </div>
                    )}

                    {/* Working day selector — shown for all presets */}
                    {(() => {
                        const selectedDays = pd.workDays || [];
                        const cap = pd.schedulePreset === 'custom'
                            ? Number(pd.workDaysPerWeek) || 7
                            : SCHEDULE_PRESETS[pd.schedulePreset]?.workDaysPerWeek ?? 5;
                        const atCap = selectedDays.length >= cap;
                        return (
                            <div className="mt-4">
                                <label className="block text-xs font-medium text-gray-600 mb-2">
                                    Which days do your employees work?
                                </label>
                                <div className="flex gap-1.5 flex-wrap">
                                    {ALL_DAYS.map(day => {
                                        const isChecked = selectedDays.includes(day.key);
                                        const isDisabled = atCap && !isChecked;
                                        return (
                                            <button
                                                key={day.key}
                                                type="button"
                                                onClick={() => handleDayToggle(day.key)}
                                                disabled={isDisabled}
                                                title={isDisabled ? `Deselect a day first — maximum ${cap} days allowed` : ''}
                                                className={`w-10 h-10 rounded-lg text-xs font-semibold border-2 transition-colors ${
                                                    isChecked
                                                        ? 'border-blue-600 bg-blue-600 text-white'
                                                        : isDisabled
                                                            ? 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
                                                            : 'border-gray-200 bg-white text-gray-500 hover:border-blue-300'
                                                }`}
                                            >
                                                {day.label}
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="text-xs text-gray-400 mt-1.5">
                                    {selectedDays.length} of {cap} day{cap !== 1 ? 's' : ''} selected
                                    {pd.schedulePreset !== 'custom' && ` · ${pd.workHoursPerDay ?? 8}h/day · ${weeklyHours}h per week`}
                                </p>
                            </div>
                        );
                    })()}
                </div>

                {/* Travel Buffer */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Shift Estimate Travel Buffer
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                        When estimating a shift from job time, this many minutes are added before the first job
                        and after the last job to account for travel time. Set to 0 to use raw job times only.
                    </p>
                    <div className="flex items-center gap-3">
                        <input
                            type="number" min="0" max="120" step="5"
                            value={pd.travelBufferMinutes ?? 30}
                            onChange={e => handleDefaultChange('travelBufferMinutes', e.target.value)}
                            className="w-24 p-2 border border-gray-300 rounded text-sm"
                        />
                        <span className="text-sm text-gray-500">minutes each side</span>
                    </div>
                </div>

                {/* Shift Tracking Mode */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Default Shift Tracking Mode</label>
                    <p className="text-xs text-gray-500 mb-3">
                        Controls what happens when an employee has no shift punch for a day.
                        Individual employees can override this in their profile.
                    </p>
                    <div className="space-y-2">
                        {[
                            {
                                value: 'strict',
                                label: 'Strict — must punch in/out',
                                desc: 'Missing shifts block timesheet approval. Admins must add the shift manually.',
                            },
                            {
                                value: 'estimate',
                                label: 'Estimate — propose from job time',
                                desc: 'Missing shifts are estimated from job times + travel buffer. Admin confirms with one click before approving.',
                            },
                            {
                                value: 'off',
                                label: 'Off — use scheduled hours',
                                desc: "Shift punches are ignored. Payroll hours come from the employee's schedule (days x hours/day). No warnings shown.",
                            },
                        ].map(opt => {
                            const active = (pd.shiftTrackingMode || 'estimate') === opt.value;
                            return (
                                <label
                                    key={opt.value}
                                    className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors
                                        ${active ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-200'}`}
                                >
                                    <input
                                        type="radio"
                                        name="shiftTrackingMode"
                                        value={opt.value}
                                        checked={active}
                                        onChange={() => handleDefaultChange('shiftTrackingMode', opt.value)}
                                        className="mt-0.5 accent-blue-600"
                                    />
                                    <div>
                                        <p className={`text-sm font-semibold ${active ? 'text-blue-700' : 'text-gray-700'}`}>
                                            {opt.label}
                                        </p>
                                        <p className="text-xs text-gray-400 mt-0.5">{opt.desc}</p>
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* ── PAID & UNPAID TIME ── paidact-ui ── */}
            <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-1">Paid &amp; Unpaid Time</h3>
                {/* abstype-6e2b9d14-h — first of two headed lists; contents below unchanged */}
                <h4 className="text-base font-semibold text-gray-800 mt-4 mb-1">Time on the clock</h4>
                <p className="text-sm text-gray-500 mb-4">
                    Choose which kinds of clocked time your employees are paid for.
                    Regular work is always paid.
                </p>
                <div className="space-y-3">
                    {PAID_ACTIVITIES.map(activity => {
                        const paid = pd.paidActivities?.[activity.key] !== false; // absent = paid
                        return (
                            <label
                                key={activity.key}
                                className={`flex items-center space-x-3 ${activity.locked ? 'cursor-default' : 'cursor-pointer'}`}
                            >
                                <input
                                    type="checkbox"
                                    checked={paid}
                                    disabled={activity.locked}
                                    onChange={() => handlePaidActivityToggle(activity.key)}
                                    className="h-4 w-4 rounded disabled:opacity-60"
                                />
                                <span className="flex flex-col">
                                    <span className="text-sm font-medium text-gray-700">
                                        {activity.label}
                                        {activity.locked && (
                                            <span className="ml-2 text-xs font-normal text-gray-400">Always paid</span>
                                        )}
                                    </span>
                                    <span className="text-xs text-gray-400">{activity.description}</span>
                                </span>
                            </label>
                        );
                    })}
                </div>

                {/* ── CUSTOM ACTIVITIES ── custact-ui ── */}
                {(() => {
                    const map = pd.customActivities || {};
                    const entries = Object.entries(map)
                        .map(([id, a]) => ({ id, ...a }))
                        .sort((x, y) => (x.createdAt || 0) - (y.createdAt || 0));
                    const active = entries.filter(a => !a.archived);
                    const archived = entries.filter(a => a.archived);
                    const atCap = active.length >= MAX_CUSTOM_ACTIVITIES;
                    const reservedLabels = new Set(PAID_ACTIVITIES.map(a => a.label.toLowerCase()));
                    const nameCounts = active.reduce((m, a) => {
                        const k = String(a.name || '').trim().toLowerCase();
                        if (k) m[k] = (m[k] || 0) + 1;
                        return m;
                    }, {});
                    return (
                        <div className="mt-6">
                            <h4 className="text-sm font-medium text-gray-700 mb-1">Your activities</h4>
                            <p className="text-xs text-gray-500 mb-3">
                                Add the kinds of time your crew clocks — each paid or unpaid.
                                These appear when an employee clocks in.
                            </p>
                            <div className="space-y-2">
                                {active.map(a => {
                                    const trimmed = String(a.name || '').trim().toLowerCase();
                                    const blank = !trimmed;
                                    const dupe = trimmed && (nameCounts[trimmed] > 1 || reservedLabels.has(trimmed));
                                    const paid = a.paid !== false;
                                    return (
                                        <div key={a.id} className="flex items-center gap-3">
                                            <div className="flex-1">
                                                <input
                                                    type="text"
                                                    value={a.name || ''}
                                                    placeholder="Activity name"
                                                    onChange={e => handleRenameCustomActivity(a.id, e.target.value)}
                                                    className={`w-full p-2 border rounded text-sm ${blank ? 'border-red-400' : 'border-gray-300'}`}
                                                />
                                                {blank && (
                                                    <p className="text-xs text-red-600 mt-1">
                                                        This activity needs a name before you can save — or archive it.
                                                    </p>
                                                )}
                                                {dupe && (
                                                    <p className="text-xs text-amber-600 mt-1">
                                                        Another activity already uses this name — techs may confuse them.
                                                    </p>
                                                )}
                                            </div>
                                            <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={paid}
                                                    onChange={() => handleToggleCustomPaid(a.id)}
                                                    className="h-4 w-4 rounded"
                                                />
                                                Paid
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => handleArchiveCustomActivity(a.id)}
                                                className="text-sm text-gray-400 hover:text-red-600"
                                            >
                                                Archive
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>

                            <button
                                type="button"
                                onClick={handleAddCustomActivity}
                                disabled={atCap}
                                title={atCap ? `Maximum ${MAX_CUSTOM_ACTIVITIES} activities — archive one to add another` : ''}
                                className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                            >
                                + Add activity
                            </button>
                            {atCap && (
                                <span className="ml-2 text-xs text-gray-400">
                                    Maximum {MAX_CUSTOM_ACTIVITIES} reached
                                </span>
                            )}

                            {archived.length > 0 && (
                                <div className="mt-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowArchivedActivities(v => !v)}
                                        className="text-xs font-medium text-gray-500 hover:text-gray-700"
                                    >
                                        {showArchivedActivities ? 'Hide' : 'Show'} archived ({archived.length})
                                    </button>
                                    {showArchivedActivities && (
                                        <div className="space-y-2 mt-2">
                                            {archived.map(a => (
                                                <div key={a.id} className="flex items-center gap-3 opacity-60">
                                                    <span className="flex-1 text-sm text-gray-500 line-through">
                                                        {a.name || '(unnamed)'}
                                                    </span>
                                                    <span className="text-xs text-gray-400">
                                                        {a.paid !== false ? 'Paid' : 'Unpaid'}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRestoreCustomActivity(a.id)}
                                                        className="text-sm text-blue-600 hover:text-blue-700"
                                                    >
                                                        Restore
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })()}

                <p className="text-sm text-gray-500 mt-6 border-t border-gray-100 pt-4">
                    These paid and unpaid settings — the four types above and your own
                    activities — apply to time clocked from now on. They won't change time
                    already recorded, including an approved or an in-progress week.
                </p>

                {/* ── TIME AWAY ── abstype-6e2b9d14-i ── not rendered until SHOW_TIME_AWAY (Seam 3) ── */}
                {SHOW_TIME_AWAY && (() => {
                    const map = pd.absenceTypes || {};
                    const entries = Object.entries(map)
                        .map(([id, a]) => ({ id, ...a }))
                        .sort((x, y) => (x.createdAt || 0) - (y.createdAt || 0));
                    const active = entries.filter(a => !a.archived);
                    const archived = entries.filter(a => a.archived);
                    const atCap = active.length >= MAX_ABSENCE_TYPES;
                    const builtInLabels = new Set(PAID_ABSENCES.map(a => a.label.toLowerCase()));
                    const nameCounts = active.reduce((m, a) => {
                        const k = String(a.name || '').trim().toLowerCase();
                        if (k) m[k] = (m[k] || 0) + 1;
                        return m;
                    }, {});
                    return (
                        <div className="mt-8 border-t border-gray-100 pt-6">
                            <h4 className="text-base font-semibold text-gray-800 mb-1">Time away</h4>
                            {/* abstype-6e2b9d14-l — founder lit check: the built-in rows have a bare checkbox, so the intro says what a tick means */}
                            <p className="text-xs text-gray-500 mb-3">
                                Choose which kinds of time off your employees are paid for — a ticked type is paid.
                                These never appear when an employee clocks in.
                            </p>
                            <div className="space-y-3">
                                {PAID_ABSENCES.map(t => {
                                    const paid = pd.paidAbsences?.[t.key] !== false; // absent = paid
                                    return (
                                        <label key={t.key} className="flex items-center space-x-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={paid}
                                                onChange={() => handlePaidAbsenceToggle(t.key)}
                                                className="h-4 w-4 rounded"
                                            />
                                            <span className="flex flex-col">
                                                <span className="text-sm font-medium text-gray-700">{t.label}</span>
                                                <span className="text-xs text-gray-400">{t.description}</span>
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>

                            <h4 className="text-sm font-medium text-gray-700 mt-6 mb-1">Your time-away types</h4>
                            <div className="space-y-2">
                                {active.map(a => {
                                    const trimmed = String(a.name || '').trim().toLowerCase();
                                    const blank = !trimmed;
                                    const clash = trimmed && builtInLabels.has(trimmed);
                                    const dupe = trimmed && !clash && nameCounts[trimmed] > 1;
                                    const paid = a.paid !== false;
                                    return (
                                        <div key={a.id} className="flex items-center gap-3">
                                            <div className="flex-1">
                                                <input
                                                    type="text"
                                                    value={a.name || ''}
                                                    placeholder="Time-away type name"
                                                    onChange={e => handleRenameAbsenceType(a.id, e.target.value)}
                                                    className={`w-full p-2 border rounded text-sm ${blank || clash ? 'border-red-400' : 'border-gray-300'}`}
                                                />
                                                {blank && (
                                                    <p className="text-xs text-red-600 mt-1">
                                                        This type needs a name before you can save — or archive it.
                                                    </p>
                                                )}
                                                {clash && (
                                                    <p className="text-xs text-red-600 mt-1">
                                                        This is already a built-in type above. Rename it before you can save — or archive it.
                                                    </p>
                                                )}
                                                {dupe && (
                                                    <p className="text-xs text-amber-600 mt-1">
                                                        Another of your time-away types already uses this name.
                                                    </p>
                                                )}
                                            </div>
                                            <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={paid}
                                                    onChange={() => handleToggleAbsencePaid(a.id)}
                                                    className="h-4 w-4 rounded"
                                                />
                                                Paid
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => handleArchiveAbsenceType(a.id)}
                                                className="text-sm text-gray-400 hover:text-red-600"
                                            >
                                                Archive
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>

                            <button
                                type="button"
                                onClick={handleAddAbsenceType}
                                disabled={atCap}
                                title={atCap ? `Maximum ${MAX_ABSENCE_TYPES} types — archive one to add another` : ''}
                                className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                            >
                                + Add time-away type
                            </button>
                            {atCap && (
                                <span className="ml-2 text-xs text-gray-400">
                                    Maximum {MAX_ABSENCE_TYPES} reached
                                </span>
                            )}

                            {archived.length > 0 && (
                                <div className="mt-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowArchivedAbsences(v => !v)}
                                        className="text-xs font-medium text-gray-500 hover:text-gray-700"
                                    >
                                        {showArchivedAbsences ? 'Hide' : 'Show'} archived ({archived.length})
                                    </button>
                                    {showArchivedAbsences && (
                                        <div className="space-y-2 mt-2">
                                            {archived.map(a => (
                                                <div key={a.id} className="flex items-center gap-3 opacity-60">
                                                    <span className="flex-1 text-sm text-gray-500 line-through">
                                                        {a.name || '(unnamed)'}
                                                    </span>
                                                    <span className="text-xs text-gray-400">
                                                        {a.paid !== false ? 'Paid' : 'Unpaid'}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRestoreAbsenceType(a.id)}
                                                        className="text-sm text-blue-600 hover:text-blue-700"
                                                    >
                                                        Restore
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            <p className="text-sm text-gray-500 mt-6 border-t border-gray-100 pt-4">
                                Paid or unpaid is copied onto each time-off record when it is entered,
                                so changing it here won't change time off already recorded.
                                Statutory holidays aren't listed here — they're worked out from the
                                province chosen under Settings → Company.
                            </p>
                        </div>
                    );
                })()}
            </div>

            {/* ── SAVE ── */}
            <div className="border-t pt-5 flex items-center justify-between">
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400"
                >
                    {isSaving ? 'Saving...' : 'Save Settings'}
                </button>
                {feedback.message && (
                    <p className={`text-sm ${feedback.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                        {feedback.message}
                    </p>
                )}
            </div>

        </div>
    );
};

export default PayrollSettings;